import { useCallback, useEffect, useState } from 'react';

/**
 * Theme resolution (PRODUCT.md, user decisions 2026-09-28).
 *
 * Precedence: the guest's own toggle (remembered in this browser) > the widget's pinned theme
 * (`/embed?theme=light|dark`, from the script tag's `data-theme`, default light) > the device.
 * The resolved choice is written to <html data-theme>; with none, the CSS `prefers-color-scheme` rules apply.
 */

export type ChatTheme = 'light' | 'dark';

const STORAGE_KEY = 'xenio.chat.theme';
const CHANGE_EVENT = 'xenio:theme';

export function resolvePinnedTheme(search: string, pathname: string): ChatTheme | undefined {
   const theme = new URLSearchParams(search).get('theme');
   if (theme === 'light' || theme === 'dark') return theme;
   return pathname.replace(/\/+$/, '') === '/embed' ? 'light' : undefined;
}

export function readStoredTheme(): ChatTheme | undefined {
   try {
      const value = window.localStorage.getItem(STORAGE_KEY);
      return value === 'light' || value === 'dark' ? value : undefined;
   } catch {
      return undefined; // storage blocked (private mode, sandboxed iframe, ...)
   }
}

function writeStoredTheme(theme: ChatTheme): void {
   try {
      window.localStorage.setItem(STORAGE_KEY, theme);
   } catch {
      // storage blocked: the choice still applies for this page view
   }
}

function deviceTheme(): ChatTheme {
   return typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** The theme the screen is showing right now. */
export function currentTheme(): ChatTheme {
   const pinned = typeof document !== 'undefined' ? document.documentElement.dataset.theme : undefined;
   if (pinned === 'light' || pinned === 'dark') return pinned;
   return deviceTheme();
}

function applyTheme(theme: ChatTheme | undefined): void {
   const root = document.documentElement;
   if (theme) root.dataset.theme = theme;
   else delete root.dataset.theme;
   window.dispatchEvent(new Event(CHANGE_EVENT));
}

/** Applied before the first paint: the guest's remembered choice, else the widget's pin, else the device. */
export function applyPinnedTheme(): void {
   if (typeof document === 'undefined') return;
   applyTheme(readStoredTheme() ?? resolvePinnedTheme(window.location.search, window.location.pathname));
}

/** The header's toggle: reads the current theme and flips it, remembering the choice in this browser. */
export function useTheme(): { theme: ChatTheme; setTheme: (theme: ChatTheme) => void; toggleTheme: () => void } {
   const [theme, setThemeState] = useState<ChatTheme>(() => (typeof document === 'undefined' ? 'light' : currentTheme()));

   useEffect(() => {
      const sync = () => setThemeState(currentTheme());
      const media = window.matchMedia?.('(prefers-color-scheme: dark)');
      window.addEventListener(CHANGE_EVENT, sync);
      media?.addEventListener?.('change', sync);
      return () => {
         window.removeEventListener(CHANGE_EVENT, sync);
         media?.removeEventListener?.('change', sync);
      };
   }, []);

   const setTheme = useCallback((next: ChatTheme) => {
      writeStoredTheme(next);
      applyTheme(next);
   }, []);

   const toggleTheme = useCallback(() => setTheme(currentTheme() === 'dark' ? 'light' : 'dark'), [setTheme]);

   return { theme, setTheme, toggleTheme };
}
